/**
 * Aanmaak-popup voor de Roadmap-matrix (roadmap-tijdreizen.md §6.4). Verschijnt
 * zodra een diagram naar de roadmap-matrix-view-kind wordt omgezet en nog niet
 * geconfigureerd is (UI-state `roadmapMatrixSetupDiagramId`). Vraagt het Program
 * (= Roadmap → bepaalt de plateau-kolommen) en het concept-type, schrijft de
 * config meteen weg en sluit — de matrix bouwt dan live op, geen Explorer-omweg
 * of hard refresh.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { resolveBilingual } from "../../registry";
import { useSession } from "../../state/session";
import { useUi } from "../../state/ui";
import { useWorkspaceData } from "../../state/workspace";
import { readRoadmapMatrixConfig } from "../canvas/roadmapMatrixConfig";
import { MATRIX_DOMAINS } from "./RoadmapMatrixConfigBlock";

export function RoadmapMatrixSetupModal() {
  const diagramId = useUi((s) => s.roadmapMatrixSetupDiagramId);
  // Per-diagram remount → frisse formulier-state (useState reset).
  if (!diagramId) return null;
  return <SetupDialog key={diagramId} diagramId={diagramId} />;
}

function SetupDialog({ diagramId }: { diagramId: string }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const close = useUi((s) => s.closeRoadmapMatrixSetup);
  const workspaceId = useSession((s) => s.workspaceId);
  const branchId = useSession((s) => s.branchId);
  const revision = useWorkspaceData((s) => s.revision);
  void revision;

  const existing = readRoadmapMatrixConfig(
    useWorkspaceData.getState().store.getDiagram(diagramId)?.viewState,
  );
  const [programId, setProgramId] = useState(existing.programId ?? "");
  const [conceptFilter, setConceptFilter] = useState(existing.conceptFilter ?? "");

  const programs = useWorkspaceData
    .getState()
    .store.listConcepts()
    .filter((c) => c.typeId === "archimate.Program" && !c.tombstoneInBranch);

  const canConfirm = Boolean(workspaceId && branchId && programId && conceptFilter);
  const confirm = () => {
    if (!workspaceId || !branchId || !programId || !conceptFilter) return;
    useWorkspaceData.getState().setRoadmapMatrixConfig(workspaceId, branchId, diagramId, {
      ...existing,
      programId,
      conceptFilter,
    });
    close();
  };

  return (
    <div className="ledger fixed inset-0 z-50 flex items-center justify-center bg-black/30">
      <div className="w-[380px] rounded-lg border border-(--ld-control) bg-(--ld-surface) p-4 shadow-xl">
        <h2 className="mb-1 font-semibold text-(--ld-text) text-sm">
          {t("roadmapMatrix.setupTitle")}
        </h2>
        <p className="mb-3 text-(--ld-muted) text-xs">{t("roadmapMatrix.subtitle")}</p>

        <label className="block text-xs text-(--ld-muted)">{t("roadmapMatrix.programLabel")}</label>
        <select
          className="mt-0.5 w-full rounded border border-(--ld-muted) bg-(--ld-surface) px-2 py-1 text-sm focus:border-(--ld-accent) focus:outline-none"
          value={programId}
          onChange={(e) => setProgramId(e.target.value)}
        >
          <option value="">{t("roadmapMatrix.programChoose")}</option>
          {programs.map((p) => (
            <option key={p.id} value={p.id}>
              {resolveBilingual(p.name, lang) || p.id}
            </option>
          ))}
        </select>
        {programs.length === 0 && (
          <div className="mt-1 text-xs text-(--ld-notice-ink) italic">
            {t("roadmapMatrix.programNone")}
          </div>
        )}

        <label className="mt-3 block text-xs text-(--ld-muted)">
          {t("roadmapMatrix.domainLabel")}
        </label>
        <select
          className="mt-0.5 w-full rounded border border-(--ld-muted) bg-(--ld-surface) px-2 py-1 text-sm focus:border-(--ld-accent) focus:outline-none"
          value={conceptFilter}
          onChange={(e) => setConceptFilter(e.target.value)}
        >
          <option value="">{t("roadmapMatrix.domainChoose")}</option>
          {MATRIX_DOMAINS.map((d) => (
            <option key={d.key} value={d.key}>
              {resolveBilingual(d.label, lang)}
            </option>
          ))}
        </select>

        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            className="rounded border border-(--ld-control) px-3 py-1.5 text-(--ld-muted) text-sm hover:bg-(--ld-raised)"
            onClick={close}
          >
            {t("settings.common.cancel")}
          </button>
          <button
            type="button"
            disabled={!canConfirm}
            className="rounded bg-(--ld-primary) px-3 py-1.5 text-sm text-(--ld-primary-ink) hover:bg-(--ld-primary-hover) disabled:opacity-40"
            onClick={confirm}
          >
            {t("roadmapMatrix.setupConfirm")}
          </button>
        </div>
      </div>
    </div>
  );
}
